// Title versions (ADR-023 section 4; contracts 1.1 section 5): a runtime row per tenant, not a pack
// item. The pack's edition is the implied version 0; servedVersion picks the one published row or the
// edition. Kept apart from training.ts because it names PackVersion, and pack.ts already imports
// training.ts (Sha256): putting it there would make the two modules import each other.
import { z } from "zod";
import { PackVersion } from "./pack.js";
import { Count, Id, IsoUtc, NonEmpty, strictObject, Ulid, version } from "./primitives.js";
import { Script } from "./render.js";
import { Sha256 } from "./training.js";
export const TitleVersionState = z.enum([
    "draft",
    "in-review",
    "approved",
    "published",
    "retired",
    "cancelled",
]);
export const TitleVersion = strictObject({
    v: version(1),
    id: Ulid,
    tenantId: Id,
    titleId: Id,
    pack: strictObject({ id: Id, version: PackVersion }), // the edition it was created against
    number: z.number().int().positive(), // 1, 2, 3 … per title per tenant
    state: TitleVersionState,
    contentVersion: NonEmpty, // D-76: what it carries when published
    script: Script, // ADR-024's strict render script: what the version is rendered from
    stills: z.array(strictObject({ id: Id, key: NonEmpty, sha256: Sha256 })),
    theme: Id,
    // The Markdown, verbatim (not trimmed); validated with the pack's question parser.
    questions: z.string().min(1),
    render: strictObject({
        jobId: Id,
        mediaKey: NonEmpty,
        sha256: Sha256,
        captionsKey: NonEmpty,
        durationSeconds: z.number().positive(),
        scenes: z.array(strictObject({
            index: Count,
            start: z.number().nonnegative(),
            end: z.number().nonnegative(),
        })),
    }).optional(),
    createdBy: NonEmpty,
    createdAt: IsoUtc,
    submittedBy: NonEmpty.optional(),
    submittedAt: IsoUtc.optional(),
    sentBackReason: NonEmpty.optional(),
    approvedBy: NonEmpty.optional(),
    approvedAt: IsoUtc.optional(),
    teachesChanged: z.boolean().optional(),
    publishedBy: NonEmpty.optional(),
    publishedAt: IsoUtc.optional(),
    retiredAt: IsoUtc.optional(),
    retiredBy: NonEmpty.optional(),
}).superRefine((t, ctx) => {
    const ids = new Set();
    t.stills.forEach((s, i) => {
        if (ids.has(s.id))
            ctx.addIssue({
                code: "custom",
                message: `title version ${t.id}: still ${s.id} appears once`,
                path: ["stills", i, "id"],
            });
        ids.add(s.id);
    });
    t.render?.scenes.forEach((s, i) => {
        if (!(s.start < s.end))
            ctx.addIssue({
                code: "custom",
                message: `title version ${t.id}: scene ${String(s.index)} starts before it ends`,
                path: ["render", "scenes", i, "end"],
            });
    });
});
//# sourceMappingURL=title-version.js.map